import { FastForward, Pause, Play, Rewind } from "lucide-react";
import * as React from "react";

import { MediaPlayer, MediaPlayerHandle } from "@/components/MediaPlayer";
import { Button, ButtonGroup } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Media, VideoProject } from "@/services/bedrock";

import { FlagTimelineMarker } from "./FlaggedIssueCard";
import { clipAppearance, MediaStrip } from "./MediaStrip";
import {
  flagQaIssueNavKey,
  formatQaTimestamp,
  focusQaIssueCard,
  lastFailQaIssueNavKey,
  lastFailTimestampMs,
  QaFlag,
} from "./qaIssues";
import {
  isVideoMedia,
  PLAYBACK_RATES,
  PlaybackRate,
  QA_SKIP_SECONDS,
  QaStudioClip,
  shouldShowTranscriptRail,
  srtCuesFromMedia,
  TimedLine,
} from "./qaStudio";
import { PlaybackCue, SkipCue } from "./studioChrome";
import { TranscriptLyrics } from "./TranscriptLyrics";

export function QaPlayerStage({
  clips,
  selectedClip,
  activeMedia,
  missingPastVideo,
  viewingPast,
  studioReadOnly,
  playing,
  playbackRate,
  currentSeconds,
  durationSeconds,
  maxPlayed,
  watchComplete,
  flags,
  displayedPriorIssues,
  selectedFlagId,
  selectedLastFailId,
  priorTimestampOverrides,
  playbackCue,
  skipCue,
  transcriptCollapsed,
  textHighlighted,
  sourceHighlighted,
  playerRef,
  timelineRef,
  onTogglePlaying,
  onPlayingChange,
  onProgress,
  onDuration,
  onSelectClip,
  onSeek,
  onSkip,
  onPlaybackRateChange,
  onSeekLine,
  onToggleTranscript,
  onMoveFlag,
  onMoveLastFail,
}: {
  clips: QaStudioClip[];
  selectedClip?: QaStudioClip;
  activeMedia?: Media;
  missingPastVideo: boolean;
  viewingPast: boolean;
  studioReadOnly: boolean;
  playing: boolean;
  playbackRate: PlaybackRate;
  currentSeconds: number;
  durationSeconds: number;
  maxPlayed: number;
  watchComplete: boolean;
  flags: QaFlag[];
  displayedPriorIssues: VideoProject.QaAuditIssue[];
  selectedFlagId: string | null;
  selectedLastFailId: number | null;
  priorTimestampOverrides: Record<number, number | null | undefined>;
  playbackCue: { id: number; playing: boolean } | null;
  skipCue: { id: number; at: number; seconds: number } | null;
  transcriptCollapsed: boolean;
  textHighlighted: boolean;
  sourceHighlighted: boolean;
  playerRef: React.Ref<MediaPlayerHandle>;
  timelineRef: React.RefObject<HTMLDivElement | null>;
  onTogglePlaying: () => void;
  onPlayingChange: (playing: boolean) => void;
  onProgress: (state: { playedSeconds: number; played: number }) => void;
  onDuration: (duration: number) => void;
  onSelectClip: (clip: QaStudioClip, seekSeconds?: number) => void;
  onSeek: (seconds: number) => void;
  onSkip: (delta: number) => void;
  onPlaybackRateChange: (rate: PlaybackRate) => void;
  onSeekLine: (line: TimedLine) => void;
  onToggleTranscript: () => void;
  onMoveFlag: (id: string, clientX: number, rect: DOMRect) => void;
  onMoveLastFail: (id: number, clientX: number, rect: DOMRect) => void;
}) {
  const selectedAppearance = selectedClip ? clipAppearance(selectedClip.source) : undefined;

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-slate-800 dark:bg-slate-950">
      <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900">
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            selectedAppearance?.headerClass ?? "bg-indigo-500/15 text-indigo-300"
          )}
        >
          {selectedAppearance?.label ?? "Edited video"}
        </span>
        {missingPastVideo ? (
          <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Video not retained</span>
        ) : null}
      </div>
      {activeMedia ? (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div
            className={cn(
              "relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden bg-slate-100 dark:bg-black [&_.mediaContainer]:flex [&_.mediaContainer]:h-full [&_.mediaContainer]:w-full [&_.mediaContainer]:items-center [&_.mediaContainer]:justify-center [&_.react-player]:!h-full [&_.react-player]:!w-full [&_video]:h-full [&_video]:max-h-full [&_video]:w-full [&_video]:max-w-full [&_video]:object-contain",
              isVideoMedia(activeMedia) && "cursor-pointer [&_.mediaContainer]:pointer-events-none"
            )}
            onClick={() => {
              if (isVideoMedia(activeMedia)) onTogglePlaying();
            }}
          >
            {isVideoMedia(activeMedia) && selectedClip ? (
              <MediaPlayer
                key={selectedClip.key}
                ref={playerRef}
                media={activeMedia}
                playing={playing}
                playbackRate={playbackRate}
                controls={false}
                objectFit="contain"
                onPlayingChange={onPlayingChange}
                onProgress={onProgress}
                onDuration={onDuration}
              />
            ) : activeMedia.uri || activeMedia.thumbnailUri ? (
              <img
                src={activeMedia.uri || activeMedia.thumbnailUri}
                alt=""
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <p className="px-4 text-center text-sm text-slate-600 dark:text-slate-300">
                This file cannot be previewed.
              </p>
            )}
            {playbackCue ? <PlaybackCue key={playbackCue.id} playing={playbackCue.playing} /> : null}
            {skipCue ? <SkipCue key={skipCue.id} seconds={skipCue.seconds} /> : null}
          </div>
          {shouldShowTranscriptRail(activeMedia, selectedClip?.source) ? (
            <TranscriptLyrics
              media={activeMedia}
              source={selectedClip?.source}
              currentSeconds={currentSeconds}
              highlighted={textHighlighted}
              collapsed={transcriptCollapsed}
              onToggleCollapsed={onToggleTranscript}
              onSeekLine={onSeekLine}
            />
          ) : null}
        </div>
      ) : (
        <p className="flex flex-1 items-center justify-center px-4 text-center text-sm text-slate-500">
          {missingPastVideo ? "Video not retained." : "No media is available."}
        </p>
      )}
      {clips.length ? (
        <MediaStrip
          clips={clips}
          selectedKey={selectedClip?.key}
          onSelect={onSelectClip}
          highlighted={sourceHighlighted}
        />
      ) : null}
      <div className="flex shrink-0 flex-col gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900">
        <div
          ref={timelineRef}
          className="relative cursor-pointer py-1.5"
          onClick={event => {
            if (!durationSeconds) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
            onSeek(ratio * durationSeconds);
          }}
        >
          <div className="relative h-2 rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-indigo-400"
              style={{ width: `${durationSeconds ? (currentSeconds / durationSeconds) * 100 : 0}%` }}
            />
            {selectedClip?.source === "EDITED"
              ? srtCuesFromMedia(activeMedia).map((cue, index) =>
                  !durationSeconds ? null : (
                    <span
                      key={`srt-tick-${index}-${cue.startMs}`}
                      title={formatQaTimestamp(cue.startMs) || undefined}
                      className="absolute top-1/2 h-1.5 w-px -translate-x-1/2 -translate-y-1/2 bg-slate-400"
                      style={{ left: `${(cue.startMs / 1000 / durationSeconds) * 100}%` }}
                    />
                  )
                )
              : null}
            {selectedClip?.source === "EDITED" ? (
              <>
                {viewingPast
                  ? null
                  : flags.map(flag =>
                      flag.timestampMs == null || !durationSeconds ? null : (
                        <FlagTimelineMarker
                          key={flag.id}
                          title={`${VideoProject.qaIssueCategoryLabel(flag.category)} ${formatQaTimestamp(
                            flag.timestampMs
                          )}`}
                          leftPercent={(flag.timestampMs / 1000 / durationSeconds) * 100}
                          selected={selectedFlagId === flag.id}
                          readOnly={studioReadOnly}
                          onSelect={() => focusQaIssueCard(flagQaIssueNavKey(flag.id))}
                          onSeek={() => onSeek(flag.timestampMs! / 1000)}
                          onMove={clientX => {
                            const rect = timelineRef.current?.getBoundingClientRect();
                            if (!rect) return;
                            onMoveFlag(flag.id, clientX, rect);
                          }}
                        />
                      )
                    )}
                {displayedPriorIssues.map(issue => {
                  const timestampMs = lastFailTimestampMs(issue, priorTimestampOverrides);
                  if (timestampMs == null || !durationSeconds) return null;
                  return (
                    <FlagTimelineMarker
                      key={lastFailQaIssueNavKey(issue.id)}
                      title={`${VideoProject.qaIssueCategoryLabel(issue.category)} ${
                        formatQaTimestamp(timestampMs) || ""
                      }`}
                      leftPercent={(timestampMs / 1000 / durationSeconds) * 100}
                      selected={selectedLastFailId === issue.id}
                      readOnly={studioReadOnly}
                      onSelect={() => focusQaIssueCard(lastFailQaIssueNavKey(issue.id))}
                      onSeek={() => onSeek(timestampMs / 1000)}
                      onMove={clientX => {
                        if (viewingPast) return;
                        const rect = timelineRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        onMoveLastFail(issue.id, clientX, rect);
                      }}
                    />
                  );
                })}
              </>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-0.5">
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              className="text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white"
              title={`Back ${QA_SKIP_SECONDS}s (J or ←)`}
              aria-label={`Skip back ${QA_SKIP_SECONDS} seconds (J or left arrow)`}
              onClick={() => onSkip(-QA_SKIP_SECONDS)}
              disabled={!isVideoMedia(activeMedia)}
            >
              <Rewind className="size-3.5" />
            </Button>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              className="text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white"
              title={playing ? "Pause (Space or K)" : "Play (Space or K)"}
              aria-label={playing ? "Pause (Space or K)" : "Play (Space or K)"}
              onClick={onTogglePlaying}
              disabled={!isVideoMedia(activeMedia)}
            >
              {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
              {formatQaTimestamp(currentSeconds * 1000) || "0:00"}
            </Button>
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              className="text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white"
              title={`Forward ${QA_SKIP_SECONDS}s (L or →)`}
              aria-label={`Skip forward ${QA_SKIP_SECONDS} seconds (L or right arrow)`}
              onClick={() => onSkip(QA_SKIP_SECONDS)}
              disabled={!isVideoMedia(activeMedia)}
            >
              <FastForward className="size-3.5" />
            </Button>
            <ButtonGroup className="ml-1 rounded-lg border border-slate-200 bg-slate-100 p-0.5 dark:border-slate-700 dark:bg-slate-800/80">
              {PLAYBACK_RATES.map(rate => (
                <Button
                  key={rate}
                  type="button"
                  size="xs"
                  variant="ghost"
                  className={cn(
                    "text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white",
                    playbackRate === rate &&
                      "bg-white text-slate-900 shadow-xs hover:bg-white hover:text-slate-900 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-700 dark:hover:text-white"
                  )}
                  title={`${rate}x playback (< slower, > faster)`}
                  aria-label={`${rate}x playback`}
                  onClick={() => onPlaybackRateChange(rate)}
                >
                  {rate}x
                </Button>
              ))}
            </ButtonGroup>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span>{watchComplete ? "Watch complete" : `${Math.round(maxPlayed * 100)}% watched`}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
